import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';

const fmt = (n) => `S/ ${Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;

export default function CTS() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcAnio, setCalcAnio] = useState(String(new Date().getFullYear()));
  const [calcMes, setCalcMes] = useState('MAYO');
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositId, setDepositId] = useState(null);
  const [depositFecha, setDepositFecha] = useState('');

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/cts', { params: { empresa_id: empresaId, periodo: periodo || undefined } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [empresaId, periodo]);

  useEffect(() => { cargar(); }, [cargar]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;

  const handleCalcular = async () => {
    setError(''); setSuccess('');
    try {
      const r = await axios.post('/api/rrhh/cts/calcular', {
        empresa_id: empresaId, periodo: `${calcAnio}-${calcMes}`, mes_pago: calcMes, anio: calcAnio
      });
      setCalcOpen(false);
      setSuccess(`CTS calculada: ${r.data.calculados} trabajadores.`);
      setPeriodo(`${calcAnio}-${calcMes}`);
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al calcular CTS'); }
  };

  const handleDepositar = async () => {
    try {
      await axios.put(`/api/rrhh/cts/${depositId}/depositar`, { fecha_deposito: depositFecha });
      setDepositOpen(false); setDepositId(null); setDepositFecha('');
      setSuccess('Depósito CTS confirmado.'); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al confirmar'); }
  };

  const totalPendiente = rows.filter(r => r.estado === 'PENDIENTE').reduce((s, r) => s + Number(r.importe || 0), 0);
  const totalDepositado = rows.filter(r => r.estado === 'DEPOSITADO').reduce((s, r) => s + Number(r.importe || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1200 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>CTS — Compensación por Tiempo de Servicios</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ p: 2, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={empresas} getOptionLabel={o => o?.razon_social || ''}
              value={empresaObj}
              onChange={(_, v) => setEmpresaId(v ? v.empresa_id : '')}
              isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
              renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
              sx={{ minWidth: '16em' }}
            />
            <TextField label="Período (ej: 2025-MAYO)" size="small" value={periodo} onChange={e => setPeriodo(e.target.value)} sx={{ minWidth: '16em' }} />
            <Box sx={{ flexGrow: 1 }} />
            <Button variant="outlined" startIcon={<CalculateIcon />} onClick={() => setCalcOpen(true)} disabled={!empresaId}>
              Calcular CTS
            </Button>
          </Box>
        </Paper>

        {rows.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip label={`Pendiente: ${fmt(totalPendiente)}`} color="warning" variant="outlined" />
            <Chip label={`Depositado: ${fmt(totalDepositado)}`} color="success" variant="outlined" />
          </Box>
        )}

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Trabajador</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Período</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rem. Computable</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Meses</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Importe CTS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Banco CTS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>F. Depósito</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Selecciona empresa y calcula la CTS para ver los resultados
                    </TableCell></TableRow>
                  ) : rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{r.trabajador_nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.num_doc}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={r.periodo} /></TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{fmt(r.rem_computable)}</TableCell>
                      <TableCell align="center">{r.meses_computables}m {r.dias_computables}d</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2e7d32' }}>{fmt(r.importe)}</TableCell>
                      <TableCell sx={{ fontSize: '0.82em' }}>{r.banco_cts || '—'}<br /><span style={{ fontFamily: 'monospace' }}>{r.num_cuenta_cts || ''}</span></TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={r.estado} color={r.estado === 'DEPOSITADO' ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.82em' }}>{r.fecha_deposito ? r.fecha_deposito.slice(0, 10) : '—'}</TableCell>
                      <TableCell align="center">
                        {r.estado === 'PENDIENTE' && (
                          <Tooltip title="Confirmar depósito">
                            <IconButton size="small" color="success" onClick={() => { setDepositId(r.id); setDepositFecha(''); setDepositOpen(true); }}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialog Calcular */}
      <Dialog open={calcOpen} onClose={() => setCalcOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Calcular CTS</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Mayo:</strong> Computa Nov – Abr (depósito hasta 15 mayo)<br />
            <strong>Noviembre:</strong> Computa May – Oct (depósito hasta 15 noviembre)
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Año" size="small" value={calcAnio} onChange={e => setCalcAnio(e.target.value)} sx={{ minWidth: '16em' }} />
            <TextField select label="Mes de pago" size="small" value={calcMes} onChange={e => setCalcMes(e.target.value)} sx={{ minWidth: '16em' }}>
              <MenuItem value="MAYO">Mayo</MenuItem>
              <MenuItem value="NOVIEMBRE">Noviembre</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalcOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<CalculateIcon />} onClick={handleCalcular}>Calcular</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Depositar */}
      <Dialog open={depositOpen} onClose={() => setDepositOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Confirmar Depósito CTS</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Fecha de Depósito" size="small" type="date" InputLabelProps={{ shrink: true }}
            value={depositFecha} onChange={e => setDepositFecha(e.target.value)} sx={{ mt: 1, minWidth: '16em' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleDepositar} disabled={!depositFecha}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
